import { H1, H2, P, UL, LI, Code, Note, Warn } from './primitives';
import { MermaidDiagram } from './MermaidDiagram';
import {
  ARCHITECTURE_CHART,
  ARCHITECTURE_LINKS,
  LIFECYCLE_CHART,
  LIFECYCLE_LINKS,
} from './architecture-charts';

export function SectionArchitecture() {
  return (
    <>
      <H1>The Big Picture</H1>
      <P>
        aiai·loop is a single Docker Compose stack with four runtime tiers.
        Each tier has one job, and the boundaries between them are what keep
        the system safe and swappable.
      </P>

      <MermaidDiagram
        chart={ARCHITECTURE_CHART}
        links={ARCHITECTURE_LINKS}
        ariaLabel="Architecture: browser to edge to the NestJS orchestrator, which owns Postgres and Redis and launches sibling agent and prototype containers over the Docker socket."
        caption="Click a node to jump to its section."
      />

      <UL>
        <LI>
          <strong className="text-ink">Edge</strong> — terminates TLS and
          routes traffic. The bundled stack ships Caddy; the Traefik deploy
          variant adds live prototype URLs on subdomains.
        </LI>
        <LI>
          <strong className="text-ink">Orchestrator</strong> — the NestJS API.
          It owns the run queue (BullMQ), persists everything to Postgres,
          streams live events over WebSocket, and launches agent containers.
        </LI>
        <LI>
          <strong className="text-ink">State</strong> — Postgres stores
          projects, issues, runs, loops, prototypes, and encrypted secrets;
          Redis backs the job queue. Postgres is the single source of truth —
          Redis can be wiped without losing history.
        </LI>
        <LI>
          <strong className="text-ink">Agent containers</strong> — throwaway
          Docker containers, one per run. Each clones the repo fresh, runs the
          agent pipeline, pushes its branch, and is deleted along with its
          workspace.
        </LI>
      </UL>

      <H2>The sibling-container model</H2>
      <P>
        The API doesn't run agents inside itself — it launches them as{' '}
        <em>sibling</em> containers on the host Docker daemon through a mounted
        Docker socket. Agents get their own locked-down sandbox with hard
        resource caps, and a runaway agent can't take the orchestrator down
        with it. The same model powers prototype preview containers.
      </P>

      <H2>Three clean seams</H2>
      <P>
        Three deliberate interfaces keep the system understandable and
        extensible:
      </P>
      <UL>
        <LI>
          <strong className="text-ink">The job spec</strong> — the API writes
          everything a run needs (repo, brief, pipeline, prompts, limits) into
          a single <Code>job.json</Code> mounted into the container. The
          container knows nothing about the orchestrator; it just executes the
          spec.
        </LI>
        <LI>
          <strong className="text-ink">The control protocol</strong> — the
          container reports back over plain stdout. Specially prefixed lines
          carry structured events (stage started, branch pushed, PR opened,
          files changed, final result) and rich display entries (agent prose,
          thinking, tool use, gate output); everything else is a raw log line.
          The API demultiplexes this stream, persists it, and re-emits it live
          to your browser.
        </LI>
        <LI>
          <strong className="text-ink">The provider seam</strong> — nothing
          outside one module knows the agent is Claude. A provider builds the
          container plan (image, env, command); adding a different AI backend
          means one new provider class and one runtime image, with queue,
          persistence, and UI untouched.
        </LI>
      </UL>

      <Note>
        Want the deep version? The repository's <Code>docs/ARCHITECTURE.md</Code>{' '}
        and <Code>docs/SECURITY.md</Code> cover the full design rationale.
      </Note>
    </>
  );
}

export function SectionRunLifecycle() {
  return (
    <>
      <H1>Anatomy of a Run</H1>
      <P>
        This is what actually happens between clicking{' '}
        <strong>Save &amp; run</strong> and a merged pull request.
      </P>

      <MermaidDiagram
        chart={LIFECYCLE_CHART}
        links={LIFECYCLE_LINKS}
        ariaLabel="Run lifecycle: brief to queue to provisioning to pipeline stage to the test gate. A failed gate loops back to the stage while budget remains, and the run fails once it is spent; a passed gate pushes a PR, records its risk, and auto-merges it once mergeable."
        caption="The test-gated loop: a failing gate feeds back into the stage until the budget runs out."
      />

      <H2>1 · Queued</H2>
      <P>
        Handing off an issue creates a run and enqueues it. A worker picks it
        up as soon as a container slot is free — the platform runs at most{' '}
        <Code>AGENT_MAX_CONCURRENCY</Code> agent containers at once; the rest
        wait in line.
      </P>

      <H2>2 · Provisioning</H2>
      <P>
        The orchestrator resolves credentials, composes the job spec, and
        launches a hardened, throwaway container. Inside, the runner configures
        git and clones the repository fresh.
      </P>

      <H2>3 · Pipeline stages</H2>
      <P>
        Each role in the pipeline takes a turn — for the default pipeline, the{' '}
        <em>Worker</em> agent plans, implements the change, and verifies it
        against the project's test suite. Every prose paragraph, thought, and
        tool call streams live to the run page.
      </P>

      <H2>4 · The gate loop</H2>
      <P>
        After a gated stage, its gate command runs — for the Worker role
        that's the project's test suite. On failure the loop feeds the exact
        gate output back and re-runs the stage, consuming one unit of the{' '}
        <strong>max loops</strong> budget (5). When the budget is exhausted,
        the default <Code>strict</Code> gate mode fails the run immediately
        with the tail of the gate output as its error — nothing waits for a
        human; in <Code>warn</Code> mode it opens the PR anyway with a
        warning — see <em>Gates &amp; Retries</em>.
      </P>

      <H2>5 · Branch, PR, risk, merge</H2>
      <P>
        On success the runner commits, pushes the <Code>aiai/…</Code> branch,
        and opens a pull request. The orchestrator then classifies the diff's
        risk (recorded for audit and written into the PR body) and
        squash-merges it as soon as the gate passed and GitHub reports it
        mergeable — see <em>Auto-merge &amp; Risk</em>.
      </P>

      <H2>6 · Wrap-up</H2>
      <P>
        The container writes its final result file — the source of truth for
        the run outcome — and is deleted together with its workspace. The
        issue's timeline records every event, and a Telegram notification fires
        if configured.
      </P>

      <Warn>
        A run that exceeds <Code>AGENT_TIMEOUT_SECONDS</Code> (default 3h) is stopped and
        marked <Code>timed_out</Code> — the branch, if already pushed, stays
        on GitHub.
      </Warn>
    </>
  );
}
